import 'intl-tel-input/build/css/intlTelInput.css';
import 'intl-tel-input/build/js/utils';

import cx from 'classnames';
import intlTelInput from 'intl-tel-input';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { FormHelperText } from '../FormHelperText';
import { withMuiError } from '../WithMuiError';
import { useStyles } from './styles';

export interface PhoneNumberFieldProps {
  id: string;
  inputNames: { phoneNumber: string; countryCode?: string };
  required: boolean;
  error?: boolean;
  onInvalid?: React.FormEventHandler<HTMLElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  countryCode?: string;
  helperText?: React.ReactNode;
  value?: string;
  defaultPhoneNumber?: string;
}

const PhoneNumberField: React.VFC<PhoneNumberFieldProps> = ({
  id,
  inputNames,
  required,
  error,
  onInvalid,
  onChange,
  countryCode,
  helperText,
  value,
  defaultPhoneNumber,
}) => {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const intl = useIntl();
  const intlTelInputRef = useRef<intlTelInput.Plugin>();
  const [phoneNumber, setPhoneNumber] = useState(value);
  const [applicantCountryCode, setApplicantcountryCode] = useState(countryCode);

  const setValidField = useCallback(
    (valid: boolean) => {
      inputRef.current?.setCustomValidity(
        valid
          ? ''
          : intl.formatMessage({
              defaultMessage: 'Valid phone number is required',
              description: 'Error message for invalid phone numbers',
            }),
      );
      if (valid) {
        let sanitizedPhoneNumber = intlTelInputRef?.current
          ?.getNumber()
          .replace(/[^a-zA-Z0-9 ]/g, '');

        if (sanitizedPhoneNumber && sanitizedPhoneNumber.startsWith('49')) {
          const phoneNumberDigits = sanitizedPhoneNumber.slice(2);
          if (phoneNumberDigits.length >= 6) {
            // the issue persists without + sign
            sanitizedPhoneNumber = `+${sanitizedPhoneNumber}`;
          }
        }
        setPhoneNumber(sanitizedPhoneNumber ?? '');
      }
    },
    [intl],
  );

  useEffect(() => {
    if (inputRef.current) {
      try {
        intlTelInputRef.current = intlTelInput(inputRef?.current, {
          initialCountry: countryCode?.toLowerCase(),
        });
      } catch {
        intlTelInputRef.current = intlTelInput(inputRef?.current, {
          initialCountry: 'us',
        });
      }
    }
    return () => intlTelInputRef?.current?.destroy();
  }, [countryCode]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target && onChange && required) {
        if (intlTelInputRef.current) {
          setPhoneNumber(event.target.value);
          const isValid = intlTelInputRef.current.isValidNumber();
          setValidField(isValid);
        }
        onChange(event);
      }
    },
    [onChange, required, setValidField],
  );

  // handle on change for when the country switches
  useEffect(() => {
    const input = inputRef.current;
    const handleCountryChange = (event: Event) => {
      // indicates to typescript that this event target is an input and so has the necessary properties for handleChange
      const currentEvent: unknown = event;
      if (input) {
        setPhoneNumber('');
        const selectedCountry =
          intlTelInputRef.current?.getSelectedCountryData();
        const newCountryCode = selectedCountry?.iso2 ?? 'us';
        setApplicantcountryCode(newCountryCode);
        handleChange(currentEvent as React.ChangeEvent<HTMLInputElement>);
      }
    };

    input?.addEventListener('countrychange', handleCountryChange);

    return () => {
      input?.removeEventListener('countrychange', handleCountryChange);
    };
  }, [handleChange, applicantCountryCode, countryCode]);

  return (
    <div className={classes.phoneNumberFieldContainer}>
      <input
        ref={inputRef}
        type="tel"
        id={id}
        autoComplete="tel"
        required={required}
        onChange={handleChange}
        className={cx(classes.phoneNumberInput, { [classes.withError]: error })}
        onInvalid={onInvalid}
        value={phoneNumber}
        defaultValue={defaultPhoneNumber}
      />
      <input type="hidden" name={inputNames.phoneNumber} value={phoneNumber} />
      <input
        type="hidden"
        name={inputNames.countryCode}
        value={applicantCountryCode}
      />
      <FormHelperText error={error}>{error && helperText}</FormHelperText>
    </div>
  );
};

const ValidatedPhoneNumberField = withMuiError(PhoneNumberField);

export { ValidatedPhoneNumberField as PhoneNumberField };
